// The foot of the sheet: the standing disclaimer on the left, and on the right
// what the customer actually pays — the converted total, the amount due under
// the document's payment terms, and the tag that names those terms.
//
// INVARIANT: the payable is stated in dollars and in roubles only. Yuan are a
// pricing currency of the rows above, never an amount the customer transfers —
// in the split settlement they are paid as dollars.
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  LOGISTICS_CALCULATION_SHEET_CHROME,
  formatSheetAmount,
  type LogisticsCalculationCurrency,
  type LogisticsCalculationEditableContent,
  type LogisticsCalculationPublicDto,
} from '@magimp/shared';
import {
  DocRadios,
  decimalPoint,
  text,
  useCell,
  useCellValue,
  useDocumentEditing,
} from './DocumentCell.js';

const doc = LOGISTICS_CALCULATION_SHEET_CHROME.document;

/**
 * The divisor as the document writes numbers, with every digit the manager
 * typed left in place — `0.780` prints as `0,780`, never renormalised to
 * `0,78`, because the customer is being shown the arithmetic that was applied.
 */
function decimalComma(value: string): string {
  return value.replace('.', ',');
}

type Dto = LogisticsCalculationPublicDto | null;

export function DocumentPayable({ dto }: { dto: Dto }) {
  return (
    <div className="doc-bottom">
      <div className="doc-disc">
        <b>{doc.disclaimer[0]}</b>
        {doc.disclaimer[1]}
      </div>
      <div className="doc-pay" data-testid="rl-document-payable">
        <div className="doc-pay-k">
          {doc.payableCaption}
          <PaymentTerms dto={dto} />
        </div>
        <div className="doc-pay-vals">
          <PayableRows dto={dto} />
        </div>
      </div>
    </div>
  );
}

/**
 * The rows the customer is asked to pay.
 *
 * INVARIANT: a document that names its settlement currency prints that row and
 * no other. One stored before the sheet offered the choice names none, and
 * keeps printing both rather than having one picked for it. The split
 * settlement prints its dollar part and its rouble part, and drops a part the
 * document charges nothing in rather than asking the customer to pay zero.
 */
function PayableRows({ dto }: { dto: Dto }) {
  const payable = dto?.payable;
  if (!payable) return null;
  const rows =
    payable.currency === 'USD_RUB'
      ? (
          [
            { converted: payable.splitUsd, amount: payable.payableSplitUsd, currency: 'USD' },
            { converted: payable.splitRub, amount: payable.payableSplitRub, currency: 'RUB' },
          ] as const
        ).filter((row) => Number(row.converted) !== 0)
      : (
          [
            { converted: payable.convertedUsd, amount: payable.payableUsd, currency: 'USD' },
            { converted: payable.convertedRub, amount: payable.payableRub, currency: 'RUB' },
          ] as const
        ).filter((row) => payable.currency === undefined || payable.currency === row.currency);
  return (
    <>
      {rows.map((row) => (
        <PayableRow
          key={row.currency}
          converted={row.converted}
          amount={row.amount}
          currency={row.currency}
          divisor={payable.divisor}
        />
      ))}
    </>
  );
}

/**
 * One payable line as the reference sheet states it: `total / divisor = result`.
 * The payment terms are named once, under the caption, not after every line.
 *
 * INVARIANT: the formula appears only when the document carries a divisor. An
 * individual payment divides by nothing, so it prints the result alone rather
 * than an equation with an empty operand.
 */
function PayableRow({
  converted,
  amount,
  currency,
  divisor,
}: {
  converted: string;
  amount: string;
  currency: LogisticsCalculationCurrency;
  divisor: string | undefined;
}) {
  return (
    <div className="doc-pay-row" data-testid="rl-payable-row">
      {divisor === undefined ? null : (
        <span className="doc-pay-src doc-n">
          {`${formatSheetAmount(converted, currency)} / ${decimalComma(divisor)} =`}
        </span>
      )}
      <span className="doc-pay-v doc-n">{formatSheetAmount(amount, currency)}</span>
    </div>
  );
}

function PaymentTerms({ dto }: { dto: Dto }) {
  const editing = useDocumentEditing();
  if (!editing) return <span className="doc-pay-mode">{dto?.paymentLabelDisplay ?? ''}</span>;
  return <EditablePaymentTerms display={dto?.paymentLabelDisplay ?? ''} />;
}

/**
 * INVARIANT: an individual payment carries no divisor at all, and the schema
 * rejects one that is merely emptied. Hiding the field is not enough — react-
 * hook-form keeps the value of an unmounted control, so the switch unregisters
 * the key outright.
 */
function EditablePaymentTerms({ display }: { display: string }) {
  const { t } = useTranslation('logisticsCalculations');
  const { unregister } = useFormContext<LogisticsCalculationEditableContent>();
  const label = useCellValue('paymentLabel');
  return (
    <span className="doc-pay-mode">
      <DocRadios
        name="paymentLabel"
        label={t('editor.fields.paymentLabel')}
        value={display}
        options={[
          { value: 'novat', label: t('paymentLabels.novat') },
          { value: 'vat_included', label: t('paymentLabels.vatIncluded') },
          { value: 'individual', label: t('paymentLabels.individual') },
        ]}
        onValueChange={(next) => {
          if (next === 'individual') unregister('paymentDivisor');
        }}
      />
      {/* The terms are a condition the sheet states, so the printed copy of the
          document carries them where the radios stand while editing. */}
      <span className="doc-radio-print-value">{display}</span>
      {/* The settlement currency decides which row prints; it is not itself a
          line of the document, so nothing of it reaches the paper. Its own
          caption keeps it from reading as two more payment terms. */}
      <span className="doc-pay-currency" data-editing="">
        <span className="doc-pay-currency-k">{t('editor.fields.payableCurrency')}</span>
        <DocRadios
          name="payableCurrency"
          label={t('editor.fields.payableCurrency')}
          value=""
          options={[
            { value: 'RUB', label: t('payableCurrencies.RUB') },
            { value: 'USD', label: t('payableCurrencies.USD') },
            { value: 'USD_RUB', label: t('payableCurrencies.USD_RUB') },
          ]}
        />
      </span>
      {label === 'individual' ? null : <DivisorField />}
    </span>
  );
}

const DIVISOR_EXAMPLE = '0,78';

function DivisorField() {
  const { t } = useTranslation('logisticsCalculations');
  const field = useCell('paymentDivisor');
  const value = text(field.value);
  return (
    <span className="doc-divisor-line" data-editing="">
      <span className="doc-pay-currency-k">{t('editor.fields.paymentDivisor')}</span>
      <input
        className="doc-input doc-divisor"
        ref={field.ref}
        aria-label={t('editor.fields.paymentDivisor')}
        aria-invalid={field.invalid || undefined}
        inputMode="decimal"
        placeholder={DIVISOR_EXAMPLE}
        value={value}
        // WHY `undefined` and not `''`: the divisor is an optional key, and an
        // empty string would be stored as a value the schema then rejects.
        onChange={(event) =>
          field.onChange(event.target.value === '' ? undefined : decimalPoint(event.target.value))
        }
        onBlur={field.onBlur}
      />
      {/* The arithmetic the field feeds, with the divisor as typed. */}
      <span className="doc-divisor-hint">
        {t('editor.fields.paymentDivisorHint', {
          divisor: value === '' ? DIVISOR_EXAMPLE : decimalComma(value),
        })}
      </span>
    </span>
  );
}
